import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import BubbleCard from '@/components/BubbleCard';
import { CheckCircle, Sparkle, ArrowRight } from '@phosphor-icons/react';

const POLL_INTERVAL = 2000;
const MAX_POLLS = 30;

const DropSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'checking', payment_status: 'pending', bubble: null });
  const attempts = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= MAX_POLLS) {
        setState((s) => ({ ...s, status: 'timeout' }));
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/drops/checkout/status/${sessionId}`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === 'paid' && data.bubble) return;
        if (data.status === 'expired') return;
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || 'status check failed');
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!sessionId) return <div className="text-center py-20 text-white/65">no session id</div>;

  const paid = state.payment_status === 'paid' && state.bubble;
  const failed = state.status === 'expired' || state.status === 'timeout';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      {!paid && !failed && (
        <>
          <div className="h-24 w-24 mx-auto rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-6">
            Materializing your <span className="shimmer-text">drop…</span>
          </h1>
          <p className="text-white/60 mt-2 text-sm">Verifying payment. This usually takes 5-30 seconds.</p>
          <div className="mt-6 text-bx-cyan text-xs uppercase tracking-widest animate-pulse">{state.status}</div>
        </>
      )}
      {paid && (
        <div className="pop-in">
          <CheckCircle size={48} weight="fill" className="text-bx-volt mx-auto" />
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-4">
            <span className="shimmer-text">Welcome, collector.</span>
          </h1>
          <p className="text-white/70 mt-2">Your bubble is minted, signed, and recorded on the ledger.</p>
          <div className="my-10 flex justify-center">
            <BubbleCard bubble={state.bubble} size="xl" showLabel={false} />
          </div>
          <h2 className="font-display text-3xl font-bold">{state.bubble.name}</h2>
          <p className="text-white/60 italic mt-2">"{state.bubble.personality?.bio}"</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="drop-success-view" onClick={() => nav(`/bubble/${state.bubble.id}`)} className="btn-primary px-6 py-3 rounded-full text-sm flex items-center gap-2">
              View on profile <ArrowRight size={14} weight="bold" />
            </button>
            <Link data-testid="drop-success-pass" to={`/pass/${state.bubble.id}`} className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition">
              Pass it forward
            </Link>
          </div>
        </div>
      )}
      {failed && (
        <>
          <h1 className="font-display text-3xl font-black tracking-tighter mt-6">Payment didn't complete</h1>
          <p className="text-white/60 mt-2 text-sm">If you were charged, your bubble will appear soon. Reach out via /contact if anything looks wrong.</p>
          <Link data-testid="drop-success-back" to="/drops" className="btn-primary px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 mt-6">
            Back to drops
          </Link>
        </>
      )}
    </div>
  );
};

export default DropSuccess;
